import { PopoverPanel, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import Link from 'next/link';

import { mapProductSubCategoryToString } from '@lib/product/constants';
import { useHeaderStore } from '@lib/stores/useHeaderStore';
import { HEADER_FILTERS } from '@lib/constants/navigations';
import { Button } from '@components/common/button';
import { Routes } from '@lib/routes';
import { slugify } from '@lib/utils';

interface LowerHeaderFiltersProps {
  onClose: () => void;
}

export function LowerHeaderFilters({
  onClose,
}: Readonly<LowerHeaderFiltersProps>) {
  const { category } = useHeaderStore();

  const filters = HEADER_FILTERS[category];

  if (!filters) return;

  return (
    <Transition
      as={Fragment}
      enter='transition ease-out duration-100'
      enterFrom='opacity-0 translate-y-1'
      enterTo='opacity-100 translate-y-0'
      leave='transition ease-in duration-100'
      leaveFrom='opacity-100 translate-y-0'
      leaveTo='opacity-0 translate-y-1'
    >
      <PopoverPanel
        as='div'
        className='absolute left-0 top-full bg-background w-full z-50 shadow-lg'
      >
        <div className='mx-auto max-w-screen-xl p-4 lg:p-6 flex gap-x-12 xl:gap-x-16'>
          {Object.entries(filters).map(([outerKey, outerValue]) => {
            if (typeof outerValue !== 'object' || outerValue === null) return;

            // Handle sub-category filters
            if ('label' in outerValue && 'param' in outerValue) {
              return (
                <div key={outerKey} className='space-y-1'>
                  <h4 className='font-bold text-lg xl:text-xl'>
                    {outerValue.label}
                  </h4>
                  <Button
                    asChild
                    variant='link'
                    className='size-auto p-0 text-base text-accent/80 hover:text-accent'
                    onClick={onClose}
                  >
                    <Link
                      href={`${Routes.collection}?subCategory=${outerValue.param}`}
                    >
                      All {outerValue.label}
                    </Link>
                  </Button>
                </div>
              );
            }

            const subCategoryLabel = mapProductSubCategoryToString(
              Number(outerKey),
            );

            // Handle identifier filters
            return (
              <div
                key={outerKey}
                className='w-full flex flex-col items-start gap-y-1'
              >
                <h4 className='font-bold text-lg xl:text-xl whitespace-nowrap'>
                  {subCategoryLabel}
                </h4>
                {Object.entries(outerValue).map(([innerKey, innerValue]) => {
                  if (typeof innerValue !== 'object' || innerValue === null)
                    return;

                  if (!('label' in innerValue && 'param' in innerValue)) return;

                  return (
                    <Button
                      key={innerKey}
                      asChild
                      variant='link'
                      className='size-auto p-0 text-base'
                      onClick={onClose}
                    >
                      <Link
                        href={`${Routes.collection}?identifiers=${innerValue.param}`}
                      >
                        {String(innerValue.label)}
                      </Link>
                    </Button>
                  );
                })}

                <Button
                  asChild
                  variant='link'
                  className='size-auto p-0 text-base text-accent/80 hover:text-accent'
                  onClick={onClose}
                >
                  <Link
                    href={`${Routes.collection}?subCategory=${slugify(subCategoryLabel)}`}
                  >
                    All {subCategoryLabel}
                  </Link>
                </Button>
              </div>
            );
          })}
        </div>
      </PopoverPanel>
    </Transition>
  );
}
